import { useEffect } from 'react'
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom'

import GuardLoadingScreen from '@/lib/provider/GuardLoadingScreen'
import usePersistedState from '@hooks/usePersistedState.ts'
import NoStoreAccess from '@routes/StoreSelection/NoStoreAccess'
import StoreSelectionRoute from '@routes/StoreSelection/StoreSelectionRoute'
import { useStore, useStoreGrants } from '@state/store.ts'
import { routesFactory, SKIP_REDIRECT_QUERY_PARAM_NAME } from '@utils/routes.ts'

/**
 * Hook that determines the current state of store selection.
 * Handles localStorage sync and validation, then returns the appropriate state.
 */
function useStoreGuardState() {
  const { data: allStores, isLoading: isLoadingAllStores } = useStoreGrants()
  const { storeId: storeIdInPathParam } = useParams<{ storeId?: string }>()
  const { error, isLoading: isLoadingSingleStore } = useStore(storeIdInPathParam)
  const [searchParams] = useSearchParams()
  const [persistedStoreId, setPersistedStoreId] = usePersistedState<string | undefined>({
    key: 'selected-store-id',
    initialState: undefined,
  })

  const skipRedirect = searchParams.get(SKIP_REDIRECT_QUERY_PARAM_NAME) === 'true'

  // Clear persisted store if user explicitly navigates to store selection (e.g., from no scans screen)
  useEffect(() => {
    if (skipRedirect && persistedStoreId) {
      setPersistedStoreId(undefined)
    }
  }, [skipRedirect, persistedStoreId, setPersistedStoreId])

  // Sync URL to localStorage when URL changes
  useEffect(() => {
    if (storeIdInPathParam) {
      setPersistedStoreId(storeIdInPathParam)
    }
  }, [storeIdInPathParam, setPersistedStoreId])

  // Validate and clear localStorage if user no longer has access
  useEffect(() => {
    if (!persistedStoreId) return

    const hasAccess = allStores.some(grant => grant.store.id === persistedStoreId)
    if (!hasAccess) {
      setPersistedStoreId(undefined)
    }
  }, [allStores, persistedStoreId, setPersistedStoreId])

  // Determine current state based on available data
  if (isLoadingAllStores) {
    return { state: 'loading' } as const
  }

  // Auto-redirect if only one store available
  if (!storeIdInPathParam && allStores.length === 1 && allStores[0]) {
    return { state: 'auto-redirect-single-store', storeId: allStores[0].store.id } as const
  }

  // No store in URL → check localStorage or show selection
  if (!storeIdInPathParam) {
    const hasAccessToPersistedStore = persistedStoreId && allStores.some(grant => grant.store.id === persistedStoreId)

    // Redirect to persisted store if accessible and not skipping
    if (hasAccessToPersistedStore && !skipRedirect) {
      return { state: 'redirect-to-persisted-store', storeId: persistedStoreId } as const
    }

    return { state: 'show-store-selection' } as const
  }

  if (isLoadingSingleStore) {
    return { state: 'loading' } as const
  }

  if (error) {
    return { state: 'no-store-access' } as const
  }

  return { state: 'store-valid' } as const
}

/**
 * Guard that ensures a store is selected before accessing protected routes.
 *
 * Flow:
 * 1. If user has only one store → auto-redirect to that store (if not explicitly skipping)
 * 2. If no store in URL but localStorage has one AND user has access → redirect to that store (if not explicitly skipping)
 * 3. If no store selected → show store selection screen
 * 4. If store in URL is invalid/inaccessible → show no access screen
 *
 */
function StoreSelectGuard() {
  const guardState = useStoreGuardState()

  switch (guardState.state) {
    case 'loading':
      return <GuardLoadingScreen />

    case 'auto-redirect-single-store':
    case 'redirect-to-persisted-store':
      return <Navigate to={routesFactory.home(guardState.storeId)} replace />

    case 'show-store-selection':
      return <StoreSelectionRoute />

    case 'no-store-access':
      return <NoStoreAccess />

    case 'store-valid':
      return <Outlet />
  }
}

export default StoreSelectGuard
